/** Resolution presets for App Store and Google Play screenshot export */

interface ExportPreset {
  readonly id: string
  readonly label: string
  readonly group: string
  readonly width: number
  readonly height: number
}

// ── App Store – iPhone ────────────────────────────────────────────
const APP_STORE_IPHONE: readonly ExportPreset[] = [
  {id: 'as-iphone-6.9', label: 'iPhone 6.9"', group: 'App Store – iPhone', width: 1320, height: 2868},
  {id: 'as-iphone-6.7', label: 'iPhone 6.7"', group: 'App Store – iPhone', width: 1290, height: 2796},
  {id: 'as-iphone-6.5', label: 'iPhone 6.5"', group: 'App Store – iPhone', width: 1284, height: 2778},
  {id: 'as-iphone-5.5', label: 'iPhone 5.5"', group: 'App Store – iPhone', width: 1242, height: 2208},
]

// ── App Store – iPad ──────────────────────────────────────────────
const APP_STORE_IPAD: readonly ExportPreset[] = [
  {id: 'as-ipad-13', label: 'iPad 13"', group: 'App Store – iPad', width: 2064, height: 2752},
  {id: 'as-ipad-11', label: 'iPad 11"', group: 'App Store – iPad', width: 1668, height: 2388},
]

// ── Google Play ───────────────────────────────────────────────────
const GOOGLE_PLAY: readonly ExportPreset[] = [
  {id: 'gp-phone', label: 'Phone (9:16)', group: 'Google Play', width: 1080, height: 1920},
  {id: 'gp-tablet', label: 'Tablet 10" (9:16)', group: 'Google Play', width: 1920, height: 3413},
  {id: 'gp-feature', label: 'Feature Graphic', group: 'Google Play', width: 1024, height: 500},
]

/** All export presets grouped for UI rendering */
export const EXPORT_PRESETS: readonly ExportPreset[] = [...APP_STORE_IPHONE, ...APP_STORE_IPAD, ...GOOGLE_PLAY]

/** Unique group labels in display order */
export const EXPORT_PRESET_GROUPS: readonly string[] = ['App Store – iPhone', 'App Store – iPad', 'Google Play']

/** Special ID for manual/custom resolution */
export const CUSTOM_PRESET_ID = 'custom'

// Hard cap on each export dimension. Mirror this value in the backend
// (be/src/config/render.ts) — the BE re-validates so the FE cannot bypass it.
// The browser export path supersamples by EXPORT_SSAA_SCALE before downscale,
// so a 3840 dimension can ask the GPU for a 7680-wide MSAA renderbuffer. Many
// drivers silently clamp such large MSAA buffers below maxTextureSize, which
// is why ExportHandler treats EXPORT_SSAA_SCALE as a TARGET and reduces it at
// render-time based on the actual drawingBufferWidth/Height.
export const MAX_EXPORT_DIMENSION = 3840
export const MIN_EXPORT_DIMENSION = 1

// Carousel: a single scene renders as one wide image at `width × N` and is
// sliced into N tiles of `width × height`. Bounds match product spec (2..5).
export const MIN_CAROUSEL_COUNT = 2
export const MAX_CAROUSEL_COUNT = 5
export const DEFAULT_CAROUSEL_COUNT = 3

// Carousel gap — empty gutter between slices, expressed as a percentage of the
// slice width so it stays consistent across export resolutions. The gutter is
// the physical spacing the App Store / Google Play insert between screenshots
// in the gallery: each slice still exports at exactly `width × height`, the
// gutter region is simply never rendered into any slice. ~5% matches the common
// design guidance (~56 px between ~1125-1242 px screenshots).
export const MIN_CAROUSEL_GAP_PCT = 0
export const MAX_CAROUSEL_GAP_PCT = 15
export const DEFAULT_CAROUSEL_GAP_PCT = 5

// Gutter width in pixels for a given slice width + gap percentage.
export const carouselGapPx = (sliceWidth: number, gapPct: number): number => Math.round((sliceWidth * gapPct) / 100)

// Heavy GL-quality knobs. ExportHandler treats EXPORT_SSAA_SCALE as a TARGET and
// clamps EXPORT_SHADOW_MAP_SIZE against gl.capabilities.maxTextureSize at render
// time, so these degrade gracefully on memory-constrained (incl. software) GPUs.
export const EXPORT_SHADOW_MAP_SIZE = 2048
export const EXPORT_SSAA_SCALE = 1
export const EXPORT_SHADOW_BIAS = -0.0001
export const EXPORT_SHADOW_NORMAL_BIAS = 0.005
export const EXPORT_SHADOW_BLUR_SAMPLES = 64

export type {ExportPreset}
